@if (notFound()) {
  <section class="sav-cmp-missing">
    <div class="sav-site-container">
      <h1>Comparison not found</h1>
      <p>This store comparison isn't available. Browse our stores to find deals instead.</p>
      <a class="sav-cmp-btn is-primary" routerLink="/storelist">Browse all stores</a>
    </div>
  </section>
} @else if (comparison(); as cmp) {
  <div class="sav-site-container">
    <div class="sav-cmp-measure">
      <app-breadcrumb [items]="crumbs()" />

      <!--
        Hero. The title and intro sit on the left, the head-to-head on the right, so the key
        numbers are on screen before any scrolling — the old hero was a pair of small logos above a
        centred paragraph, and the reader had to reach the table to learn anything at all.
      -->
      <section class="sav-cmp-hero" aria-label="Stores compared">
        <div class="sav-cmp-hero-copy">
          @if (categoryLabel()) {
            <span class="sav-cmp-hero-eyebrow">{{ categoryLabel() }}</span>
          }

          <h1 class="sav-cmp-title">{{ heading() }}</h1>

          @if (cmp.intro) {
            <div class="sav-cmp-intro" [innerHTML]="cmp.intro | trustedCmsHtml"></div>
          }

          @if (updatedLabel() !== '—') {
            <span class="sav-cmp-updated">
              <span class="sav-cmp-dot" aria-hidden="true"></span>
              Data updated {{ updatedLabel() }}
            </span>
          }
        </div>

        <div class="sav-cmp-vs">
          @for (side of heroSides(); track side.store.id; let last = $last) {
            <a
              class="sav-cmp-vs-store"
              [class.is-leader]="side.isLeader"
              [routerLink]="side.store.path"
            >
              @if (side.isLeader) {
                <span class="sav-cmp-vs-flag">Leads on {{ side.wins }} of {{ side.total }}</span>
              }

              <span class="sav-cmp-vs-logo">
                @if (side.store.logoImagePath) {
                  <img
                    [src]="side.store.logoImagePath | assetUrl"
                    [alt]="side.store.name + ' logo'"
                    width="84"
                    height="84"
                    loading="eager"
                  />
                } @else {
                  <span class="sav-cmp-vs-initials">{{ initials(side.store.name) }}</span>
                }
              </span>

              <span class="sav-cmp-vs-name">{{ side.store.name }}</span>

              <span class="sav-cmp-vs-stats">
                @for (stat of side.stats; track stat.label) {
                  <span class="sav-cmp-vs-stat">
                    <strong>{{ stat.value }}</strong>
                    <span>{{ stat.label }}</span>
                  </span>
                }
              </span>
            </a>
            @if (!last) {
              <span class="sav-cmp-vs-badge" aria-hidden="true">VS</span>
            }
          }
        </div>
      </section>

      <!-- Jump links: the page runs past 5,000px, so the sections need to be reachable without
           scrolling through all of them. -->
      <nav class="sav-cmp-jump" aria-label="Sections on this page">
        @for (link of jumpLinks(); track link.id) {
          <!-- `[routerLink]="[]"` (the current route) + `fragment`, NOT a bare `href="#id"`: with
               `<base href="/">` in index.html a fragment-only href resolves against the base, so
               `#cmp-table` navigates to the home page. -->
          <a [routerLink]="[]" [fragment]="link.id">{{ link.label }}</a>
        }
      </nav>

      <!-- Side-by-side table -->
      <section class="sav-cmp-section" id="cmp-table" aria-label="Side-by-side comparison">
        <app-section-heading
          heading="Side-by-side comparison"
          subtitle="Live figures from both stores — the highlighted cell wins the row."
        />

        <div class="sav-cmp-table-wrap">
          <table class="sav-cmp-table">
            <thead>
              <tr>
                <th scope="col" class="sav-cmp-th-feature">Feature</th>
                @for (store of [cmp.storeA, cmp.storeB]; track store.id) {
                  <th scope="col">
                    <span class="sav-cmp-th-store">
                      <span class="sav-cmp-th-logo">
                        @if (store.logoImagePath) {
                          <img [src]="store.logoImagePath | assetUrl" [alt]="" loading="lazy" />
                        } @else {
                          {{ initials(store.name) }}
                        }
                      </span>
                      {{ store.name }}
                    </span>
                  </th>
                }
              </tr>
            </thead>
            <tbody>
              @for (row of metricRows(); track row.key) {
                <tr>
                  <th scope="row">{{ row.label }}</th>
                  <td [class.is-winner]="isWinner(row.key, cmp.storeA.id)">
                    {{ row.valueA }}
                    @if (isWinner(row.key, cmp.storeA.id)) {
                      <!-- The tick is decorative; the fact is repeated for screen readers. -->
                      <span class="sav-cmp-tick" aria-hidden="true">✓</span>
                      <span class="sav-cmp-sr-only">best value</span>
                    }
                  </td>
                  <td [class.is-winner]="isWinner(row.key, cmp.storeB.id)">
                    {{ row.valueB }}
                    @if (isWinner(row.key, cmp.storeB.id)) {
                      <span class="sav-cmp-tick" aria-hidden="true">✓</span>
                      <span class="sav-cmp-sr-only">best value</span>
                    }
                  </td>
                </tr>
              }
            </tbody>
          </table>
        </div>

        @if (winnerSummary()) {
          <p class="sav-cmp-summary">{{ winnerSummary() }}</p>
        }
        <p class="sav-cmp-note">
          A dash (—) means we don't have verified data for that metric yet. Coupon figures are
          refreshed from live store data.
        </p>
      </section>

      <!-- Admin-authored content -->
      @if (cmp.sections.length) {
        @for (section of cmp.sections; track section.heading) {
          @if (section.heading && section.body) {
            <section class="sav-cmp-section">
              <!-- No subtitle: these are admin-authored and carry only a heading and a body. -->
              <app-section-heading [heading]="section.heading" />
              <div class="sav-cmp-longform" [innerHTML]="section.body | trustedCmsHtml"></div>
            </section>
          }
        }
      }

      <!-- Pros & cons -->
      <section class="sav-cmp-section" id="cmp-pros-cons" aria-label="Pros and cons">
        <app-section-heading
          heading="Pros &amp; cons"
          subtitle="What each store is good at, and where it falls short."
        />
        <div class="sav-cmp-duo">
          @for (
            entry of [
              { store: cmp.storeA, pc: cmp.storeAProsCons },
              { store: cmp.storeB, pc: cmp.storeBProsCons },
            ];
            track entry.store.id
          ) {
            <article class="sav-cmp-card">
              <h3 class="sav-cmp-card-head">
                <span class="sav-cmp-card-logo">
                  @if (entry.store.logoImagePath) {
                    <img [src]="entry.store.logoImagePath | assetUrl" [alt]="" loading="lazy" />
                  } @else {
                    {{ initials(entry.store.name) }}
                  }
                </span>
                {{ entry.store.name }}
              </h3>

              @if (entry.pc.pros.length) {
                <p class="sav-cmp-pc-label is-pro">Pros</p>
                <ul class="sav-cmp-pros">
                  @for (pro of entry.pc.pros; track pro) {
                    <li><span class="sav-cmp-mark" aria-hidden="true">✓</span>{{ pro }}</li>
                  }
                </ul>
              }

              @if (entry.pc.cons.length) {
                <p class="sav-cmp-pc-label is-con">Cons</p>
                <ul class="sav-cmp-cons">
                  @for (con of entry.pc.cons; track con) {
                    <li><span class="sav-cmp-mark" aria-hidden="true">✗</span>{{ con }}</li>
                  }
                </ul>
              }

              @if (!entry.pc.pros.length && !entry.pc.cons.length) {
                <p class="sav-cmp-empty">No pros and cons recorded yet.</p>
              }
            </article>
          }
        </div>
      </section>

      <!-- Store statistics -->
      <section class="sav-cmp-section" id="cmp-stats" aria-label="Store statistics">
        <app-section-heading
          heading="Store statistics"
          subtitle="Reach, ratings and catalogue size for each retailer."
        />
        <div class="sav-cmp-duo">
          @for (block of statBlocks(); track block.store.id) {
            <article class="sav-cmp-card">
              <h3 class="sav-cmp-card-head">
                <span class="sav-cmp-card-logo">
                  @if (block.store.logoImagePath) {
                    <img [src]="block.store.logoImagePath | assetUrl" [alt]="" loading="lazy" />
                  } @else {
                    {{ initials(block.store.name) }}
                  }
                </span>
                <a [routerLink]="block.store.path">{{ block.store.name }}</a>
              </h3>
              <dl class="sav-cmp-stat-rows">
                @for (row of block.rows; track row.label) {
                  <div class="sav-cmp-stat-row">
                    <dt>{{ row.label }}</dt>
                    <dd>{{ row.value }}</dd>
                  </div>
                }
              </dl>
            </article>
          }
        </div>
      </section>

      <!-- Poll -->
      <section class="sav-cmp-section" id="cmp-poll" aria-label="Reader poll">
        <app-section-heading
          heading="Which store do you rate higher?"
          subtitle="Vote once to see how other shoppers have called it."
        />
        <div class="sav-cmp-poll">
          @if (poll(); as p) {
            <div class="sav-cmp-poll-options">
              @for (store of [cmp.storeA, cmp.storeB]; track store.id) {
                <button
                  type="button"
                  class="sav-cmp-poll-option"
                  [class.is-voted]="votedStoreId() === store.id"
                  [attr.aria-pressed]="votedStoreId() === store.id"
                  [disabled]="hasVoted() || voteSubmitting()"
                  (click)="vote(store)"
                >
                  <span class="sav-cmp-poll-logo">
                    @if (store.logoImagePath) {
                      <img [src]="store.logoImagePath | assetUrl" [alt]="" loading="lazy" />
                    } @else {
                      {{ initials(store.name) }}
                    }
                  </span>
                  {{ store.name }}
                </button>
              }
            </div>

            <!-- Always in the DOM so a screen reader has a region to announce into: a live region
                 inserted at the same moment as its content is usually missed. -->
            <p class="sav-cmp-sr-only" aria-live="polite">{{ pollAnnouncement() }}</p>

            @if (hasVoted()) {
              <div class="sav-cmp-poll-results">
                @for (store of [cmp.storeA, cmp.storeB]; track store.id) {
                  <div class="sav-cmp-poll-result">
                    <p class="sav-cmp-poll-row">
                      <span>{{ store.name }}</span>
                      <span class="sav-cmp-poll-percent"
                        >{{ store.id === cmp.storeA.id ? p.storeAPercent : p.storeBPercent }}%</span
                      >
                    </p>
                    <span class="sav-cmp-poll-track" aria-hidden="true">
                      <span
                        class="sav-cmp-poll-fill"
                        [class.is-b]="store.id === cmp.storeB.id"
                        [style.width.%]="
                          store.id === cmp.storeA.id ? p.storeAPercent : p.storeBPercent
                        "
                      ></span>
                    </span>
                  </div>
                }
                <p class="sav-cmp-poll-total">
                  {{ p.totalVotes }} {{ p.totalVotes === 1 ? 'vote' : 'votes' }}
                </p>
              </div>
            }

            @if (voteError()) {
              <p class="sav-cmp-poll-error">{{ voteError() }}</p>
            }
          }
        </div>
      </section>

      <!-- FAQs -->
      @if (cmp.faqs.length) {
        <section class="sav-cmp-section" id="cmp-faqs" aria-label="Frequently asked questions">
          <app-section-heading
            heading="Frequently asked questions"
            [subtitle]="
              'Common questions about ' + cmp.storeA.name + ' and ' + cmp.storeB.name + '.'
            "
          />
          <!-- Shared FAQ accordion — see `.sav-faq-*` in styles.scss. -->
          <div class="sav-faq-list">
            @for (faq of cmp.faqs; track faq.question; let i = $index) {
              <div class="sav-faq-item" [class.expanded]="expandedFaq() === i">
                <h3 class="sav-faq-heading">
                  <button
                    type="button"
                    class="sav-faq-question"
                    [attr.aria-expanded]="expandedFaq() === i"
                    [attr.aria-controls]="'cmp-faq-a-' + i"
                    (click)="toggleFaq(i)"
                  >
                    {{ faq.question }}
                  </button>
                </h3>
                <!-- The admin FAQ editor is rich text, so the answer arrives as CMS HTML. -->
                <div class="sav-faq-answer" [class.visible]="expandedFaq() === i">
                  <div
                    class="sav-faq-answer-body"
                    [id]="'cmp-faq-a-' + i"
                    [innerHTML]="faq.answer | trustedCmsHtml"
                  ></div>
                </div>
              </div>
            }
          </div>
        </section>
      }

      <!-- Final verdict -->
      @if (cmp.verdict) {
        <section class="sav-cmp-verdict" id="cmp-verdict" aria-labelledby="cmp-verdict-heading">
          <span class="sav-cmp-verdict-eyebrow">Final verdict</span>
          <h2 id="cmp-verdict-heading">Which store is better?</h2>
          <div class="sav-cmp-verdict-body" [innerHTML]="cmp.verdict | trustedCmsHtml"></div>

          @if (verdictScores().length) {
            <div class="sav-cmp-verdict-chips">
              @for (chip of verdictScores(); track chip.name) {
                <span class="sav-cmp-chip-score"
                  >{{ chip.name }} · {{ chip.score }} popularity</span
                >
              }
            </div>
          }
        </section>
      }

      <!-- Explore more -->
      <section class="sav-cmp-section sav-cmp-explore" aria-label="Explore more">
        <app-section-heading
          heading="Explore more"
          subtitle="Open either store's coupon page, or read another head-to-head."
        />

        <div class="sav-cmp-links">
          @for (store of [cmp.storeA, cmp.storeB]; track store.id) {
            <a class="sav-cmp-store-link" [routerLink]="store.path">
              <span class="sav-cmp-store-link-logo">
                @if (store.logoImagePath) {
                  <img [src]="store.logoImagePath | assetUrl" [alt]="" loading="lazy" />
                } @else {
                  {{ initials(store.name) }}
                }
              </span>
              <span class="sav-cmp-store-link-copy">
                <strong>{{ store.name }}</strong>
                <span>{{ num(store.activeCoupons) }} active coupons</span>
              </span>
              <span class="sav-cmp-store-link-go" aria-hidden="true">→</span>
            </a>
          }
        </div>

        @if (cmp.relatedComparisons.length) {
          <h3 class="sav-cmp-related-title">Related comparisons</h3>
          <div class="sav-cmp-related">
            @for (rel of cmp.relatedComparisons; track rel.id) {
              <a class="sav-cmp-related-card" [routerLink]="rel.path">
                <span class="sav-cmp-related-logos" aria-hidden="true">
                  @for (store of [rel.storeA, rel.storeB]; track store.id) {
                    <span class="sav-cmp-related-logo">
                      @if (store.logoImagePath) {
                        <img [src]="store.logoImagePath | assetUrl" [alt]="" loading="lazy" />
                      } @else {
                        {{ initials(store.name) }}
                      }
                    </span>
                  }
                </span>
                <span class="sav-cmp-related-name">{{ rel.title }}</span>
                <span class="sav-cmp-related-go" aria-hidden="true">→</span>
              </a>
            }
          </div>
        }
      </section>
    </div>
  </div>
} @else if (isLoading()) {
  <section class="sav-cmp-section">
    <div class="sav-site-container">
      <p class="sav-cmp-empty">Loading comparison…</p>
    </div>
  </section>
}
